import React, { MouseEventHandler } from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Close } from '@material-ui/icons';

export type ConfirmationDialogProps = {
  open: boolean;
  onClose?: MouseEventHandler;
  onConfirm?: MouseEventHandler;
  dialogText?: string;
  dialogContent?: JSX.Element;
  dialogTitle?: string;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  showCloseButton?: boolean;
  autoFocus?: boolean;
  loading?: boolean;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleContainer: {
      height: theme.spacing(8),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
    },
    dialogContent: {
      padding: theme.spacing(1, 4, 0, 4),
    },
    buttonContainer: {
      height: theme.spacing(8),
      alignItems: 'center',
      padding: theme.spacing(0, 4),
      '& > :not(:first-child)': {
        marginLeft: theme.spacing(2),
      },
    },
  }),
);

export const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  dialogText,
  dialogContent,
  dialogTitle = 'ARE YOU SURE?',
  secondaryActionLabel = 'Cancel',
  primaryActionLabel = 'Delete',
  showCloseButton = false,
  autoFocus = true,
  loading = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
}: ConfirmationDialogProps): JSX.Element => {
  const classes = useStyles();
  return (
    <Dialog
      open={open}
      fullWidth={true}
      onClose={onClose}
      disableBackdropClick={disableBackdropClick}
      disableEscapeKeyDown={disableEscapeKeyDown}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      data-testid="alert-dialog"
    >
      <div className={classes.titleContainer}>
        <DialogTitle id="alert-dialog-title" data-testid="alert-title">
          <Typography variant="body1" color="textSecondary">
            {dialogTitle}
          </Typography>
        </DialogTitle>
        {showCloseButton && (
          <IconButton color="secondary" aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        )}
      </div>
      <Divider />
      {dialogContent ? (
        dialogContent
      ) : (
        <DialogContent className={classes.dialogContent}>
          <DialogContentText id="alert-dialog-description">{dialogText}</DialogContentText>
        </DialogContent>
      )}
      {!loading ? <Divider /> : <LinearProgress />}
      <DialogActions className={classes.buttonContainer}>
        <Button
          variant="outlined"
          onClick={onClose}
          color="secondary"
          data-testid="cancel-action"
          aria-label="cancel-delete"
        >
          {secondaryActionLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          autoFocus={autoFocus}
          data-testid="confirm-action"
          aria-label="confirm-delete"
        >
          {primaryActionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
